import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'redux/schemas';
import axios from 'axios';
import { EditableElements, EditableTrueType } from 'redux/schemas/models/edit-translations';
import { LectureComponent } from 'redux/schemas/models/lecture-component';
import { CourseOutlineItem } from 'redux/selectors/course-outline/course-outline-item';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { Attachment } from 'redux/schemas/models/attachment';
import t from 'react-translate';

type TranslationComponent = {
  type: EditableElements,
  trueType?: EditableTrueType,
  component: LectureComponent | CourseOutlineItem,
};

export const showEditTranslationComponent = createAction<TranslationComponent>('SHOW_EDIT_TRANSLATION_COMPONENT');
export const closeEditTranslationComponent = createAction('CLOSE_EDIT_TRANSLATION_COMPONENT');

type FileReplacementParameters = {
  lectureComponentId: number,
  attachmentId: number,
  attachment,
  catalogId,
  language,
};

type TranslationUpdateParams = {
  id: number,
  attributes: {
    translationId: number,
    name: string,
    text: string,
    elementType?: string,
    elementId?: number,
    // For surveys, single view quizzes and full view quizzes
    questionId?: number,
    optionId?: number,
    feedbackId?: number;
    // For assignments
    attachments?: Attachment[],
  }[],
  language?: string,
  attachments?: {
    fileName: string,
    fileSize: number,
    fileType: string,
    uniqueId: string,
    translatedFrom: number,
  }[],
};

export const translatedFileReplacement = createAsyncThunk(
  'TRANSLATED_FILE_REPLACEMENT',
  async (params: FileReplacementParameters, thunkAPI) => axios.post(
    `${params.catalogId}/attachments/${params.attachmentId}/create_manual_translation`,
    {
      attachment: params.attachment,
      language: params.language,
    },
  ).then(res => res.data.result)
    .catch(() => {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.AUTOMATED_TRANSLATION.ERROR(),
        message: t.AUTOMATED_TRANSLATION.RETRY(),
      }));
    }),
);

export const translationUpdate = createAsyncThunk(
  'TRANSLATION_UPDATE',
  async (params: TranslationUpdateParams, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    return axios.put(
      '/translations/batch_update',
      {
        translations: params.attributes.map((translation) => ({
          id: translation.translationId,
          text: translation.text,
        })),
        attachments: params.attachments,
        language: params.language,
      },
    ).then(res => res.data.result)
      .catch(() => {
        thunkAPI.dispatch(addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.AUTOMATED_TRANSLATION.ERROR(),
          message: t.AUTOMATED_TRANSLATION.RETRY(),
        }));
      });
  },
);

export const translationUpdateSections = createAsyncThunk(
  'TRANSLATION_UPDATE_SECTIONS',
  async (params: any, thunkAPI) => axios.put(
    '/translations/update_by_owner',
    {
      ownerType: params.type,
      ownerId: params.id,
      language: params.language,
      attributes: params.attributes,
    },
  ).then(res => res.data.result)
    .catch(() => {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.AUTOMATED_TRANSLATION.ERROR(),
        message: t.AUTOMATED_TRANSLATION.RETRY(),
      }));
    }),
);
